// Alto que le queda a un elemento hasta el borde inferior de la ventana.
//
// Es lo que permite que la tabla de líneas se coma todo el espacio libre sin
// hardcodear la suma de cabecera + pestañas + toolbar: se mide dónde ha caído el
// contenedor y se resta. Así aguanta que la cabecera crezca (un cliente con
// nombre largo, un aviso de datos parciales) o que cambie la resolución.
//
// Se recalcula al redimensionar y cuando cambia de tamaño cualquier cosa por
// encima, que es lo que mueve el punto de partida.
import { useEffect, useState, type RefObject } from 'react'

interface Opciones {
  /** Hueco que se deja por debajo, para que la tabla no muera pegada al borde. */
  margenInferior?: number
  /** Por debajo de esto no se encoge: con la ventana muy baja es preferible que
   *  la página crezca a dejar una tabla de dos filas. */
  minimo?: number
}

export function useAltoDisponible(
  ref: RefObject<HTMLElement | null>,
  { margenInferior = 24, minimo = 260 }: Opciones = {},
): number | undefined {
  const [alto, setAlto] = useState<number>()

  useEffect(() => {
    const nodo = ref.current
    if (!nodo) return

    const medir = () => {
      const top = nodo.getBoundingClientRect().top
      setAlto(Math.max(minimo, window.innerHeight - top - margenInferior))
    }

    medir()
    window.addEventListener('resize', medir)

    // El punto de partida se mueve si crece algo de arriba (toolbar que pasa a
    // dos líneas, chip de filtro que aparece), no sólo si cambia la ventana.
    const ro = new ResizeObserver(medir)
    if (nodo.parentElement) ro.observe(nodo.parentElement)
    const main = document.querySelector('main')
    if (main) ro.observe(main)

    return () => {
      window.removeEventListener('resize', medir)
      ro.disconnect()
    }
  }, [ref, margenInferior, minimo])

  return alto
}
